import { createSelector } from "@reduxjs/toolkit";

import { type PlaybookConfig } from "components/Generative/Pages/Playbooks/types";
import { AdaApiSlice } from "slices";

import { setToastNotification } from "./playbooks";
import { type Playbook, type PlaybooksResponse } from "./types";

export const PlaybooksApi = AdaApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createPlaybook: builder.mutation({
      query: (data) => ({
        url: "playbooks",
        method: "POST",
        data,
      }),
      invalidatesTags: ["Playbooks"],
    }),
    getPlaybooks: builder.query<PlaybooksResponse, void>({
      query: () => ({
        url: "playbooks",
        method: "GET",
      }),
      providesTags: ["Playbooks"],
    }),
    getPlaybook: builder.query<Playbook, string>({
      query: (id) => ({
        url: `playbooks/${id}`,
        method: "GET",
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          dispatch(
            setToastNotification({
              message: "Error fetching playbook",
              isError: true,
            }),
          );
        }
      },
      providesTags: ["Playbooks"],
    }),
    updatePlaybook: builder.mutation<
      void,
      { id: string; config: PlaybookConfig }
    >({
      query: (data) => ({
        url: `playbooks/${data.id}`,
        method: "PATCH",
        data: {
          name: data.config.name,
          description: data.config.description,
          content: data.config.content,
          is_active: data.config.is_active,
          rules: data.config.rules,
        },
      }),
      invalidatesTags: ["Playbooks"],
    }),
    updatePlaybookActive: builder.mutation<
      void,
      { id: string; is_active: boolean }
    >({
      query: (data) => ({
        url: `playbooks/${data.id}`,
        method: "PATCH",
        data: {
          is_active: data.is_active,
        },
      }),
      invalidatesTags: ["Playbooks"],
    }),
    deletePlaybook: builder.mutation({
      query: (id) => ({
        url: `playbooks/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Playbooks"],
    }),
    generatePlaybook: builder.mutation({
      query: (data) => {
        const isFileIncluded = data instanceof FormData;

        return {
          url: "playbooks/generate",
          method: "POST",
          data,
          formData: isFileIncluded,
        };
      },
    }),
  }),
});

export const SelectAllPlaybooks = createSelector(
  PlaybooksApi.endpoints.getPlaybooks.select(),
  (res) => res.data?.playbooks ?? [],
);

export const {
  useCreatePlaybookMutation,
  useGetPlaybooksQuery,
  useGetPlaybookQuery,
  useUpdatePlaybookMutation,
  useUpdatePlaybookActiveMutation,
  useDeletePlaybookMutation,
  useGeneratePlaybookMutation,
} = PlaybooksApi;
